import { arc as d3Arc } from "d3-shape";
import { graphemeCount, toWellFormed } from "./text";

export * from "./text";

/** A status in a palette. `order` encodes progression; `isTerminal` marks "satisfied". */
export interface DialStatusRef {
  key: string;
  colour: string;
  order: number;
  isTerminal?: boolean;
}

/**
 * A pie segment within a section. Provide either a resolved `colour`, or `itemStatusKeys`
 * + a model `palette` so the segment colour is rolled up from its items.
 */
export interface SegmentModel {
  key: string;
  label: string;
  /** The segment's full name, for screen readers when its short label is empty. */
  name?: string;
  weight?: number;
  colour?: string;
  itemStatusKeys?: string[];
}

/** An outer-ring section subdivided into segments. */
export interface SectionModel {
  key: string;
  label: string;
  weight?: number;
  segments: SegmentModel[];
  /** The section's own status colour (KEY-479), drawn as a coloured edge along the outside of the section
   * band rather than as its fill — the band stays the brand colour so the labels stay legible. */
  edgeColour?: string;
  /** The same edge cut into one arc per segment (KEY-494), in this section's segment order. Present
   * means draw these instead of {@link edgeColour}'s single arc; a segment with no colour leaves a gap
   * rather than being guessed at. */
  edgeSegments?: { segmentKey: string; colour?: string }[];
  /** The section's own band colour (KEY-755), `#rrggbb`; absent means {@link DIAL_COLOURS.sectionFill}.
   * The label takes whichever of light/dark reads on it, and the status edge is still drawn on top. */
  colour?: string | null;
}

/** The full dial: an outer label ring + inner status ring around a central hub. */
export interface DialModel {
  sections: SectionModel[];
  palette?: DialStatusRef[];
  /** Inner edge of the inner (status) ring. */
  innerRadius: number;
  /** Boundary between inner status ring and outer label ring. */
  midRadius: number;
  /** Outer edge of the outer (label) ring. */
  outerRadius: number;
  /** Gap between sections, in radians (applied at section boundaries). Default 0. */
  sectionGap?: number;
  /** Whether to render the central completion ring + percentage. Default true. */
  showCentralProgress?: boolean;
  /** Precomputed completion fraction (0–1) for the hub ring. When set, overrides the value derived
   * from segments' itemStatusKeys — used by itemised dials whose completion is rolled up server-side. */
  completion?: number;
}

export interface SectionArc {
  sectionKey: string;
  label: string;
  path: string;
  centroid: [number, number];
  startAngle: number;
  endAngle: number;
  /** The section's status colour (KEY-479), when it has one. */
  edgeColour?: string;
  /** The per-segment split of that edge (KEY-494), when the dial draws one. */
  edgeSegments?: { segmentKey: string; colour?: string }[];
  /** The section's own band colour (KEY-755), when it has a valid one. */
  colour?: string;
}

export interface SegmentArc {
  sectionKey: string;
  segmentKey: string;
  label: string;
  /** The segment's full name, when the model gave one. */
  name?: string;
  path: string;
  fill: string;
  /** The rolled-up status key for this segment (for status-aware labels), if resolvable. */
  statusKey?: string;
  centroid: [number, number];
  startAngle: number;
  endAngle: number;
}

export interface DialLayout {
  sectionArcs: SectionArc[];
  segmentArcs: SegmentArc[];
  /** Fraction (0–1) of items that are terminal across the whole dial; 0 when unknown. */
  completion: number;
}

const FALLBACK_FILL = "#cbd5e1"; // slate-300
const TAU = Math.PI * 2;

const weightOf = (w: number | undefined): number => (w === undefined ? 1 : Math.max(w, 0));

/**
 * Resolve a segment's representative status from its items, matching the canonical
 * precedence: if every item is terminal → the highest-order terminal status; otherwise
 * the highest-order non-terminal status present. Returns null when nothing matches.
 */
export function rollupSegmentStatus(
  itemStatusKeys: readonly string[],
  palette: readonly DialStatusRef[],
): DialStatusRef | null {
  const byKey = new Map(palette.map((s) => [s.key, s]));
  const statuses = itemStatusKeys.map((k) => byKey.get(k)).filter((s): s is DialStatusRef => s !== undefined);
  if (statuses.length === 0) {
    return null;
  }

  const highestOrder = (list: DialStatusRef[]) => list.reduce((a, b) => (b.order > a.order ? b : a));
  const nonTerminal = statuses.filter((s) => !s.isTerminal);
  return nonTerminal.length === 0 ? highestOrder(statuses) : highestOrder(nonTerminal);
}

/** Compute the two-ring SVG geometry (outer section labels + inner segment statuses). */
export function computeDialLayout(model: DialModel): DialLayout {
  const { sections, palette, innerRadius, midRadius, outerRadius, sectionGap = 0 } = model;

  const outerArc = d3Arc<{ startAngle: number; endAngle: number }>().innerRadius(midRadius).outerRadius(outerRadius);
  const innerArc = d3Arc<{ startAngle: number; endAngle: number }>().innerRadius(innerRadius).outerRadius(midRadius);

  const sectionTotal = sections.reduce((sum, s) => sum + weightOf(s.weight), 0) || 1;
  const halfGap = sectionGap / 2;
  const byKey = palette ? new Map(palette.map((s) => [s.key, s])) : undefined;

  const sectionArcs: SectionArc[] = [];
  const segmentArcs: SegmentArc[] = [];
  let terminalItems = 0;
  let totalItems = 0;

  let cursor = 0;
  for (const section of sections) {
    const span = (weightOf(section.weight) / sectionTotal) * TAU;
    const start = cursor + halfGap;
    const end = cursor + span - halfGap;
    cursor += span;

    if (end > start) {
      const datum = { startAngle: start, endAngle: end };
      sectionArcs.push({
        sectionKey: section.key,
        label: section.label,
        path: outerArc(datum) ?? "",
        centroid: outerArc.centroid(datum),
        startAngle: start,
        endAngle: end,
        edgeColour: section.edgeColour,
        edgeSegments: section.edgeSegments,
        colour: normaliseSectionColour(section.colour) ?? undefined,
      });
    }

    const segTotal = section.segments.reduce((sum, seg) => sum + weightOf(seg.weight), 0) || 1;
    let segCursor = start;
    for (const segment of section.segments) {
      const segSpan = (weightOf(segment.weight) / segTotal) * (end - start);
      const segStart = segCursor;
      const segEnd = segCursor + segSpan;
      segCursor = segEnd;
      const datum = { startAngle: segStart, endAngle: segEnd };

      const rolled = segment.itemStatusKeys && palette ? rollupSegmentStatus(segment.itemStatusKeys, palette) : null;
      segmentArcs.push({
        sectionKey: section.key,
        segmentKey: segment.key,
        label: segment.label,
        name: segment.name,
        path: innerArc(datum) ?? "",
        fill: segment.colour ?? rolled?.colour ?? FALLBACK_FILL,
        statusKey: rolled?.key,
        centroid: innerArc.centroid(datum),
        startAngle: segStart,
        endAngle: segEnd,
      });

      if (segment.itemStatusKeys && byKey) {
        for (const key of segment.itemStatusKeys) {
          totalItems += 1;
          if (byKey.get(key)?.isTerminal) {
            terminalItems += 1;
          }
        }
      }
    }
  }

  const derivedCompletion = totalItems === 0 ? 0 : terminalItems / totalItems;
  return {
    sectionArcs,
    segmentArcs,
    completion: model.completion ?? derivedCompletion,
  };
}

/** Build a standalone donut-arc path (used e.g. for the hub completion ring). */
export function arcPath(innerRadius: number, outerRadius: number, startAngle: number, endAngle: number): string {
  return (
    d3Arc<{ startAngle: number; endAngle: number }>()
      .innerRadius(innerRadius)
      .outerRadius(outerRadius)({ startAngle, endAngle }) ?? ""
  );
}

/** Rotation (degrees) so a label at the given angle reads radially outward. */
export function radialLabelRotation(startAngle: number, endAngle: number): number {
  const midDeg = (((startAngle + endAngle) / 2) * 180) / Math.PI;
  return midDeg <= 180 ? midDeg - 90 : midDeg + 90;
}

/** Convenience: total number of segments across all sections. */
export function countSegments(model: DialModel): number {
  return model.sections.reduce((sum, s) => sum + s.segments.length, 0);
}

// --- Colour contrast (WCAG 2.1) ---

/** sRGB relative luminance (0–1) of a `#rgb`/`#rrggbb` colour. Returns 0 for unparseable input. */
export function relativeLuminance(hex: string): number {
  const c = hex.replace("#", "").trim();
  const full = c.length === 3 ? c.split("").map((x) => x + x).join("") : c;
  if (!/^[0-9a-fA-F]{6}$/.test(full)) {
    return 0;
  }
  const lin = (i: number) => {
    const s = parseInt(full.slice(i, i + 2), 16) / 255;
    return s <= 0.03928 ? s / 12.92 : Math.pow((s + 0.055) / 1.055, 2.4);
  };
  return 0.2126 * lin(0) + 0.7152 * lin(2) + 0.0722 * lin(4);
}

/** WCAG contrast ratio (1–21) between two colours. */
export function contrastRatio(a: string, b: string): number {
  const la = relativeLuminance(a);
  const lb = relativeLuminance(b);
  const hi = Math.max(la, lb);
  const lo = Math.min(la, lb);
  return (hi + 0.05) / (lo + 0.05);
}

/** Returns whichever of `light`/`dark` has the higher contrast against `background`. */
export function readableTextColour(background: string, light = "#ffffff", dark = "#1a1a1a"): string {
  return contrastRatio(background, light) >= contrastRatio(background, dark) ? light : dark;
}

/** Whether `foreground` on `background` meets a WCAG threshold (AA normal text = 4.5, large = 3). */
export function meetsContrast(foreground: string, background: string, minimum = 4.5): boolean {
  return contrastRatio(foreground, background) >= minimum;
}

// --- Static export (SVG / PNG) ---

/**
 * The dial's presentational palette. Single source of truth shared by the live React
 * component and the static exporter so the two can never drift.
 */
export const DIAL_COLOURS = {
  /** Outer section ring fill (brand neutral). */
  sectionFill: "#525254",
  /** Outer section ring fill when selected. */
  sectionFillSelected: "#3a3a3c",
  /** Section label text. */
  sectionLabel: "#ffffff",
  /** Centre hub disc. */
  hub: "#ffffff",
  /** Completion-ring track (unfilled portion). */
  ringTrack: "#e0e0e0",
  /** Completion-ring fill below 100%. */
  gold: "#FDBD15",
  /** Completion-ring fill at 100%. */
  complete: "#229954",
  /** Centre percentage text. */
  centreText: "#1a1a1a",
  /** Centre sub-label text. */
  centreSubText: "#666666",
} as const;

// --- Section colours (KEY-755) ---

/**
 * The swatches an author picks a section's band colour from (KEY-755). Soft, mutually distinct, and clear of the
 * status reds/ambers/greens so a coloured band never reads as a status. Every one gives its label (light or dark,
 * see {@link sectionLabelColour}) a contrast of at least 6:1.
 *
 * PLACEHOLDER: the designer's palette from the "Key Dials — gentle themes design handoff" will replace these.
 * Swap the hexes here — nothing else stores them as a list: a section keeps only its chosen `#rrggbb`, so a
 * colour already picked keeps showing after the swap (it just stops being one of the offered swatches).
 */
export const SECTION_SWATCHES: readonly { readonly name: string; readonly hex: string }[] = [
  { name: "Soft blue", hex: "#8fb3de" },
  { name: "Teal", hex: "#7fc4bf" },
  { name: "Sage", hex: "#a8c39a" },
  { name: "Sand", hex: "#e2cfa4" },
  { name: "Apricot", hex: "#f0b48e" },
  { name: "Rose", hex: "#e4a6b6" },
  { name: "Lavender", hex: "#b8a9dc" },
  { name: "Slate", hex: "#4f6478" },
  { name: "Ochre", hex: "#d6aa4c" },
  { name: "Plum", hex: "#74506f" },
];

/** A section colour as the server stores it — lower-case `#rrggbb` (a `#rgb` is expanded) — or null for
 * anything else, which the dial treats as "no colour". Mirrors `DialSection.NormaliseColour`. */
export function normaliseSectionColour(colour: string | null | undefined): string | null {
  let value = (colour ?? "").trim().toLowerCase();
  if (/^#[0-9a-f]{3}$/.test(value)) {
    value = "#" + value.slice(1).split("").map((c) => c + c).join("");
  }
  return /^#[0-9a-f]{6}$/.test(value) ? value : null;
}

/** The fill for a section band: its own colour when it has one, else `fallback` (the platform's section
 * colour — a CSS variable on the live dial, {@link DIAL_COLOURS.sectionFill} in the static export). */
export function sectionBandFill(colour: string | null | undefined, fallback: string = DIAL_COLOURS.sectionFill): string {
  return normaliseSectionColour(colour) ?? fallback;
}

/** The label colour on a section band: light or dark, whichever reads on the section's own colour, else
 * `fallback` (the default band is dark, so its label is white). */
export function sectionLabelColour(colour: string | null | undefined, fallback: string = DIAL_COLOURS.sectionLabel): string {
  const hex = normaliseSectionColour(colour);
  return hex ? readableTextColour(hex, "#ffffff", "#1a1a1a") : fallback;
}

/** How much of the section band's depth a section's status edge takes (KEY-479). Shared with the live
 * dial so the exported SVG and the screen draw the same edge. */
export const SECTION_EDGE_FRACTION = 0.16;

/**
 * The gap between two arcs of a split section edge (KEY-494), as a fraction of each arc's own span and
 * capped in radians so a wide arc does not grow a wide gap.
 *
 * A gap rather than abutting arcs because without one two neighbours at the same status merge into a
 * single band, which is exactly the reading the split exists to prevent.
 */
export const SECTION_EDGE_SPLIT_GAP_FRACTION = 0.08;
export const SECTION_EDGE_SPLIT_GAP_MAX = 0.02;

/** One arc of a split section edge, inset by half the gap at each end. Shared by the live dial and the
 * exporter so the screen and the PDF cut the ring in the same places. */
export function splitEdgeArc(startAngle: number, endAngle: number): { startAngle: number; endAngle: number } {
  const span = endAngle - startAngle;
  const gap = Math.min(span * SECTION_EDGE_SPLIT_GAP_FRACTION, SECTION_EDGE_SPLIT_GAP_MAX);
  return { startAngle: startAngle + gap / 2, endAngle: endAngle - gap / 2 };
}

/** Completion-ring fill: green once fully complete, otherwise gold. */
export function completionFillColour(completion: number): string {
  return Math.round(completion * 100) >= 100 ? DIAL_COLOURS.complete : DIAL_COLOURS.gold;
}

/** Itemised-dial traffic-light band, as returned by the server rollup. */
export type RollupBand = "Red" | "Amber" | "Green";

/** Segment fill colours for itemised dials, keyed by rolled-up RAG band. Chosen to match the
 * existing status palette (none/incomplete/complete) so itemised and simple dials feel consistent. */
export const RAG_COLOURS: Record<RollupBand, string> = {
  Red: "#c0392b",
  Amber: "#d68910",
  Green: "#229954",
} as const;

/** Maps an itemised segment's rolled-up band to its fill colour. */
export function bandColour(band: RollupBand): string {
  return RAG_COLOURS[band];
}

/**
 * Colour for an itemised segment's rolled-up {@link RollupBand}, taken from a specific palette so a
 * per-segment palette override tints the segment (KEY-187) instead of the fixed {@link RAG_COLOURS}.
 * The band maps onto the palette's own progression by status `order`:
 * - **Red** ("none complete") → the least-advanced status;
 * - **Green** ("all complete") → the terminal ("done") status, else the highest-order status;
 * - **Amber** ("some complete") → the furthest-along non-terminal status between those two.
 *
 * Falls back to {@link RAG_COLOURS} for a band the palette can't distinctly supply (empty palette, or
 * no middle status for Amber — e.g. a two-status not-started/done palette). Accepts any {@link DialStatusRef}-
 * shaped statuses, so callers can pass their richer status objects directly.
 */
export function bandPaletteColour(band: RollupBand, statuses: readonly DialStatusRef[] | undefined): string {
  if (!statuses || statuses.length === 0) {
    return RAG_COLOURS[band];
  }
  const ordered = [...statuses].sort((a, b) => a.order - b.order);
  const first = ordered[0];
  const done = [...ordered].reverse().find((s) => s.isTerminal) ?? ordered[ordered.length - 1];
  if (band === "Red") {
    return first.colour;
  }
  if (band === "Green") {
    return done.colour;
  }
  // Amber: the highest-order status that is neither the start nor a terminal ("done") status.
  const middle = ordered.filter((s) => s.key !== first.key && s.key !== done.key && !s.isTerminal);
  return middle.length ? middle[middle.length - 1].colour : RAG_COLOURS.Amber;
}

const SVG_PAD = 8;
const MIN_SEGMENT_LABEL_SPAN = 22; // px arc-length below which a segment label is hidden
const DEFAULT_FONT_FAMILY =
  "system-ui, -apple-system, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif";

export interface SvgExportOptions {
  /** Square pixel size for the root `<svg>` width/height. Default 520 (matches the live component). */
  size?: number;
  /** Label shown under the completion percentage in the hub. Default "COMPLETE". */
  centreLabel?: string;
  /** Optional solid background colour (e.g. "#ffffff" for email/PDF). Default: transparent. */
  background?: string;
  /** Font family applied to all text. Default a cross-platform sans-serif stack. */
  fontFamily?: string;
}

const XML_ESCAPES: Record<string, string> = {
  "&": "&amp;",
  "<": "&lt;",
  ">": "&gt;",
  '"': "&quot;",
  "'": "&apos;",
};

function escapeXml(value: string): string {
  // Well-formed first (KEY-757): a lone surrogate from a half-cut emoji is not a legal XML character.
  return toWellFormed(value).replace(/[&<>"']/g, (c) => XML_ESCAPES[c] ?? c);
}

/** Round to 3 decimals and normalise -0, keeping serialized coordinates compact. */
function fmt(value: number): string {
  const r = Math.round(value * 1000) / 1000;
  return Object.is(r, -0) ? "0" : String(r);
}

function svgLabel(
  centroid: readonly [number, number],
  startAngle: number,
  endAngle: number,
  text: string,
  opts: { fontSize: number; fill: string; fontWeight?: number },
): string {
  const rotation = radialLabelRotation(startAngle, endAngle);
  const weight = opts.fontWeight ? ` font-weight="${opts.fontWeight}"` : "";
  return (
    `<text transform="translate(${fmt(centroid[0])},${fmt(centroid[1])}) rotate(${fmt(rotation)})" ` +
    `text-anchor="middle" dominant-baseline="central" font-size="${opts.fontSize}"${weight} ` +
    `fill="${opts.fill}">${escapeXml(text)}</text>`
  );
}

/**
 * Serialize a {@link DialModel} to a standalone, static SVG string — no DOM, no React, no
 * interactivity — so it can run server-side (notification thumbnails) or client-side (PDF and
 * email previews). The geometry and styling mirror the live `Dial` component.
 */
export function toSvgString(model: DialModel, options: SvgExportOptions = {}): string {
  const { size = 520, centreLabel = "COMPLETE", background, fontFamily = DEFAULT_FONT_FAMILY } = options;
  const layout = computeDialLayout(model);

  const extent = model.outerRadius + SVG_PAD;
  const hubRadius = model.innerRadius;
  const ringOuter = hubRadius * 0.66;
  const ringInner = ringOuter - 8;
  const completionPct = Math.round(layout.completion * 100);

  const body: string[] = [];

  if (background) {
    body.push(
      `<rect x="${fmt(-extent)}" y="${fmt(-extent)}" width="${fmt(extent * 2)}" height="${fmt(extent * 2)}" fill="${background}"/>`,
    );
  }

  // Inner ring — segment status colours.
  for (const arc of layout.segmentArcs) {
    body.push(`<path d="${arc.path}" fill="${arc.fill}" stroke="rgba(255,255,255,0.6)" stroke-width="0.8"/>`);
    if ((arc.endAngle - arc.startAngle) * model.midRadius > MIN_SEGMENT_LABEL_SPAN) {
      body.push(svgLabel(arc.centroid, arc.startAngle, arc.endAngle, arc.label, { fontSize: 8, fill: readableTextColour(arc.fill) }));
    }
  }

  // Outer ring — section bands: the brand fill, or each section's own colour (KEY-755).
  for (const arc of layout.sectionArcs) {
    // KEY-755: the section's own colour when it has one; the status edge below is still drawn over it.
    body.push(`<path d="${arc.path}" fill="${sectionBandFill(arc.colour)}" stroke="#ffffff" stroke-width="1.5"/>`);
    // A section's own status (KEY-479): a coloured edge along the outside of the band, the same
    // proportion of the band the live dial uses so the PDF and the screen agree.
    const edge = (model.outerRadius - model.midRadius) * SECTION_EDGE_FRACTION;
    if (arc.edgeSegments?.length) {
      // KEY-494: the same edge, one arc per segment. Angles come from the segment arcs already laid out
      // for this section, so the edge lines up with the action point beneath it.
      const segs = layout.segmentArcs.filter((sa) => sa.sectionKey === arc.sectionKey);
      for (const seg of arc.edgeSegments) {
        const at = segs.find((sa) => sa.segmentKey === seg.segmentKey);
        if (!at || !seg.colour) {
          continue;
        }
        const { startAngle, endAngle } = splitEdgeArc(at.startAngle, at.endAngle);
        body.push(
          `<path d="${arcPath(model.outerRadius - edge, model.outerRadius, startAngle, endAngle)}" fill="${seg.colour}"/>`,
        );
      }
    } else if (arc.edgeColour) {
      body.push(
        `<path d="${arcPath(model.outerRadius - edge, model.outerRadius, arc.startAngle, arc.endAngle)}" fill="${arc.edgeColour}"/>`,
      );
    }
    body.push(
      svgLabel(arc.centroid, arc.startAngle, arc.endAngle, arc.label, {
        // One visible character (a letter or a whole emoji, KEY-757) is drawn large, like a mnemonic.
        fontSize: graphemeCount(arc.label) > 1 ? 10 : 14,
        fontWeight: 700,
        fill: sectionLabelColour(arc.colour),
      }),
    );
  }

  // Centre hub + completion ring.
  body.push(`<circle r="${fmt(hubRadius)}" fill="${DIAL_COLOURS.hub}"/>`);
  body.push(`<path d="${arcPath(ringInner, ringOuter, 0, TAU)}" fill="${DIAL_COLOURS.ringTrack}"/>`);
  if (completionPct > 0) {
    body.push(`<path d="${arcPath(ringInner, ringOuter, 0, TAU * layout.completion)}" fill="${completionFillColour(layout.completion)}"/>`);
  }
  body.push(
    `<text text-anchor="middle" dominant-baseline="central" y="-6" font-size="22" font-weight="700" fill="${DIAL_COLOURS.centreText}">${completionPct}%</text>`,
  );
  body.push(
    `<text text-anchor="middle" dominant-baseline="central" y="16" font-size="9" fill="${DIAL_COLOURS.centreSubText}" letter-spacing="1">${escapeXml(centreLabel)}</text>`,
  );

  return (
    `<svg xmlns="http://www.w3.org/2000/svg" width="${fmt(size)}" height="${fmt(size)}" ` +
    `viewBox="${fmt(-extent)} ${fmt(-extent)} ${fmt(extent * 2)} ${fmt(extent * 2)}" ` +
    `font-family="${fontFamily}" role="img" aria-label="${escapeXml(`Key dial — ${completionPct}% complete`)}">` +
    body.join("") +
    `</svg>`
  );
}

/** Minimal structural shape of a canvg instance (`.render()`). */
export interface CanvgInstance {
  render(): Promise<void> | void;
}

/** Minimal structural shape of the `Canvg` class — `Canvg.fromString(ctx, svg, options?)`. */
export interface CanvgStatic {
  fromString(context: unknown, svg: string, options?: unknown): CanvgInstance;
}

/**
 * Minimal structural shape of a canvas. Satisfied by a browser `HTMLCanvasElement`
 * (`toDataURL`) and by node-canvas (`toBuffer`).
 */
export interface ExportCanvas {
  width: number;
  height: number;
  getContext(contextId: "2d"): unknown;
  toBuffer?(mimeType: string): Uint8Array;
  toDataURL?(mimeType?: string): string;
}

export interface PngExportDeps {
  /** The `Canvg` class from the `canvg` package. */
  Canvg: CanvgStatic;
  /** A canvas instance: `document.createElement("canvas")` in the browser, or node-canvas server-side. */
  canvas: ExportCanvas;
  /** Extra options forwarded to `Canvg.fromString` (e.g. node-canvas presets). */
  canvgOptions?: unknown;
}

function dataUrlToBytes(dataUrl: string): Uint8Array {
  const base64 = dataUrl.slice(dataUrl.indexOf(",") + 1);
  if (typeof atob !== "function") {
    throw new Error("toPng: cannot decode a data URL — use a canvas that exposes toBuffer instead.");
  }
  const binary = atob(base64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i += 1) {
    bytes[i] = binary.charCodeAt(i);
  }
  return bytes;
}

/**
 * Rasterise a {@link DialModel} to PNG bytes via canvg. The renderer stays free of a hard
 * canvg/canvas dependency: the caller injects the `Canvg` class plus a platform canvas
 * (node-canvas for server thumbnails, `HTMLCanvasElement` in the browser), keeping this
 * package lightweight and usable in either environment.
 */
export async function toPng(model: DialModel, deps: PngExportDeps, options: SvgExportOptions = {}): Promise<Uint8Array> {
  const { Canvg, canvas, canvgOptions } = deps;
  const size = options.size ?? 520;
  canvas.width = size;
  canvas.height = size;

  const context = canvas.getContext("2d");
  if (!context) {
    throw new Error("toPng: canvas.getContext('2d') returned null.");
  }

  const instance = Canvg.fromString(context, toSvgString(model, options), canvgOptions);
  await instance.render();

  if (typeof canvas.toBuffer === "function") {
    return canvas.toBuffer("image/png");
  }
  if (typeof canvas.toDataURL === "function") {
    return dataUrlToBytes(canvas.toDataURL("image/png"));
  }
  throw new Error("toPng: canvas exposes neither toBuffer nor toDataURL.");
}
